import {exam_getExaminationInfoList_Api, exam_getExaminations_Api} from 'apis'
import classNames from 'classnames'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useAsyncWithCache from 'modules/asyncCache/useAsyncWithCache'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useState} from 'react'
import {branch, compose, renderNothing} from 'recompose'
import AntdConfigWrapper, {RenderEmptyMeta,} from 'views/AppLocals/custom/AntdConfigWrapper'
import {reactSelectComponents, SelectWidget,} from 'views/AppLocals/custom/ReactSelectComponents'
import {bindQueryParam, bindQueryParams,} from 'views/AppLocals/functions/routerHelper'
import {getResponseItems, NullAsDefault} from 'views/Shared'

const FilterSelectField = ({
  name,
  value,
  onChange,
  options,
  isLoading,
  className,
  ...props
}) => (
  <AntdConfigWrapper renderEmpty={RenderEmptyMeta}>
    <SelectWidget
      value={_.find(options, { value }) ? value : undefined}
      showSearch={true}
      allowClear={true}
      onChange={(value) => {
        onChange(name, value)
      }}
      loading={!!isLoading}
      optionFilterProp="label"
      className={classNames('CenterSelector w-full', className)}
      options={!!isLoading ? [] : options}
      suffixIcon={
        !!isLoading ? undefined : <reactSelectComponents.DropdownIndicator />
      }
      {...props}
    />
  </AntdConfigWrapper>
)

const ExaminationsAsyncWrapper = compose(
  branch(
    ({ owner_id, owner_type }) => _.some([owner_id, owner_type], _.isEmpty),
    renderNothing
  )
)(({ owner_id, owner_type, children }) => {
  const [values, setValues] = useState()

  const apiInfo = exam_getExaminations_Api

  const { response, isLoading } = useAsyncWithCache({
    cacheId: [
      apiInfo.path.replace(':id', owner_id).replace(':type', owner_type),
      JSON.stringify(values),
    ].join('/'),
    apiInfo,
    values,
    deps: [values],
    query: bindQueryParams([{ id: owner_id }, { type: owner_type }]),
  })

  return _.isFunction(children)
    ? children({
        isLoading,
        items: getResponseItems(response),
        onSearch: (keyword) => {
          setValues({
            keyword,
          })
        },
      })
    : children
})

const ExaminationsInfoAsyncWrapper = compose(
  branch(({ examination_id }) => _.isEmpty(examination_id), renderNothing)
)(({ examination_id, children }) => {
  const [values, setValues] = useState()

  const apiInfo = exam_getExaminationInfoList_Api

  const { response, isLoading } = useAsyncWithCache({
    cacheId: [
      apiInfo.path.replace(':id', examination_id),
      JSON.stringify(values),
    ].join('/'),
    apiInfo,
    values,
    deps: [values],
    query: bindQueryParam({ id: examination_id }),
  })

  return _.isFunction(children)
    ? children({
        isLoading,
        items: getResponseItems(response),
        onSearch: (keyword) => {
          setValues({
            keyword,
          })
        },
      })
    : children
})

export const ExaminationGroupReportFilter = ({
  owner_id,
  owner_type,
  defaultValue,
  value: filter = {
    examination_id: undefined,
    examination_info_id: undefined,
  },
  onChange: setValues = NullAsDefault,
}) => {
  const t = useTranslate()

  const handleChange = useCallback(
    (name, value) => {
      setValues({
        ...filter,
        [name]: value,
      })
    },
    [filter]
  )

  return (
    <div className="flex flex-col items-center gap-2 mt-2">
      <ExaminationsAsyncWrapper
        {...{
          owner_id,
          owner_type,
        }}>
        {({ items, isLoading }) => {
          const options = Array.from(items).map(({ id, ...item }) => ({
            value: id,
            label: t(getTitle(item)),
          }))
          return (
            <div className="flex flex-col gap-1 w-full">
              <span className="font-medium text-2xs text-slate-400 uppercase tracking-wider">
                  {`${t('choose examination')} `}
              </span>
              <FilterSelectField
                name="examination_id"
                loading={isLoading}
                value={filter.examination_id}
                placeholder={t(isLoading ? 'loading...' : 'examination')}
                options={options}
                onChange={(name, value) => {
                  handleChange(name, value)
                }}
              />
            </div>
          )
        }}
      </ExaminationsAsyncWrapper>

      <ExaminationsInfoAsyncWrapper
        {...{ examination_id: filter.examination_id }}>
        {({ items, isLoading }) => {
          const options = Array.from(items).map(({ id, label }) => ({
            value: id,
            label: t(label).replace('?', ''),
          }))
          return (
            <div className="flex flex-col gap-1 w-full">
              <span className="font-medium text-2xs text-slate-400 uppercase tracking-wider">
                {t('content is filtered by')}{': '}
              </span>
              <FilterSelectField
                name="examination_info_id"
                loading={isLoading}
                value={filter.examination_info_id}
                placeholder={t(
                  isLoading ? 'loading...' : 'ex: filter report exam'
                )}
                options={options}
                onChange={(name, value) => {
                  handleChange(name, value)
                }}
              />
            </div>
          )
        }}
      </ExaminationsInfoAsyncWrapper>
    </div>
  )
}

export default ExaminationGroupReportFilter
